import { useState, memo, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { CloseIcon, ArrowBackIcon } from 'src/themes/cosmos/icons';
import FloatingOverlay from 'src/themes/cosmos/components/FloatingOverlay';
import IconButton from 'src/themes/cosmos/components/IconButton';
import { MEDIA_TYPE, SETTING_CONTAINERS as SETTING_CONTAINER_NAME, TRANSMISSION_TYPE } from 'src/interfaces/enums';
import useResolution from 'src/hooks/useResolution';
import usePlayback from 'src/hooks/usePlayback';
import useSubtitle from 'src/hooks/useSubtitle';
import useTranslation from 'next-translate/useTranslation';
import useClickOutside, { ClickOutsideEvent } from 'src/hooks/useClickOutside';
import useApplicationData from 'src/hooks/useApplicationData';
import MainSettingControl from '../../components/MainSettingControl';
import ResolutionControlContainer from './components/ResolutionControlContainer';
import PlaybackRateControlContainer from './components/PlaybackRateControlContainer';
import SubtitleControlContainer from './components/SubtitleControlContainer';
import styles from './styles.module.scss';

export interface ISettingsOverlayProps {
  visible: boolean;
  onHide: () => void;
}

const SettingsOverlay = ({ visible, onHide }: ISettingsOverlayProps) => {
  const [container, setContainer] = useState<SETTING_CONTAINER_NAME>(SETTING_CONTAINER_NAME.MAIN);
  const { resolution, hasNativeHLS } = useResolution();
  const { playbackRateLabel } = usePlayback();
  const { subtitleLabel, subtitles } = useSubtitle();
  const { mediaType, transmissionType } = useApplicationData();
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);

  const isLive = transmissionType === TRANSMISSION_TYPE.live;
  const isAudio = mediaType === MEDIA_TYPE.audio;

  const handleClickOutside = useCallback(
    (event: ClickOutsideEvent) => {
      if (visible) {
        event.stopImmediatePropagation();
        setTimeout(onHide, 100);
      }
    },
    [visible, onHide],
  );

  useClickOutside(containerRef?.current, handleClickOutside);

  useEffect(() => {
    setContainer(SETTING_CONTAINER_NAME.MAIN);
  }, [visible]);

  const SETTING_CONTAINERS = {
    main: {
      component: (
        <MainSettingControl
          onChange={setContainer}
          subtitle={{ value: subtitleLabel, visible: Boolean(subtitles?.length) }}
          resolution={{ value: resolution.label, visible: !hasNativeHLS && !isAudio }}
          speed={{ value: playbackRateLabel, visible: !isLive }}
        />
      ),
      title: t('settings.main'),
    },
    speed: {
      component: <PlaybackRateControlContainer hideSettingMenu={onHide} />,
      title: t('settings.speed'),
    },
    resolution: {
      component: <ResolutionControlContainer hideSettingMenu={onHide} />,
      title: t('settings.quality'),
    },
    subtitle: {
      component: <SubtitleControlContainer hideSettingMenu={onHide} />,
      title: t('settings.subtitle.title'),
    },
  };

  return (
    <div
      className={classNames(styles.container, { [styles.visible]: visible })}
      data-testid='settings-overlay'
      ref={containerRef}
    >
      <FloatingOverlay
        title={SETTING_CONTAINERS[container].title}
        leftIcon={
          container !== SETTING_CONTAINER_NAME.MAIN && (
            <IconButton
              aria-label='go back'
              icon={<ArrowBackIcon />}
              className={styles.backButton}
              onClick={() => {
                setContainer(SETTING_CONTAINER_NAME.MAIN);
              }}
            />
          )
        }
        rightIcon={
          <IconButton
            aria-label='close settings'
            icon={<CloseIcon />}
            className={styles.closeButton}
            onClick={onHide}
          />
        }
      >
        {SETTING_CONTAINERS[container].component}
      </FloatingOverlay>
    </div>
  );
};

export default memo(SettingsOverlay);
